import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Search, User, ChevronDown } from "lucide-react";
import { Logo } from "@/components/Logo";
import { AnnouncementBar } from "@/components/layout/AnnouncementBar";
import { SearchDialog } from "@/components/SearchDialog";
import { NAV } from "@/data/content";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [location.pathname]);

  const dedupNav = NAV.filter((n, i, arr) => arr.findIndex((x) => x.label === n.label) === i);

  return (
    <>
      <AnnouncementBar />
      <header
        data-testid="site-header"
        className={`sticky top-0 z-50 transition-colors duration-300 ${
          scrolled ? "bg-white/85 backdrop-blur-xl border-b border-border shadow-sm" : "bg-white border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Logo />
            <nav className="hidden lg:flex items-center gap-7">
              {dedupNav.map((item) => item.children ? (
                <DropdownMenu key={item.label}>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                      className="inline-flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-navy transition-colors focus:outline-none"
                    >
                      {item.label}<ChevronDown className="h-3.5 w-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    {item.children.map((c) => (
                      <DropdownMenuItem
                        key={c.label}
                        onClick={() => navigate(c.to)}
                        data-testid={`nav-${c.label.toLowerCase().replace(/[\s&]+/g, "-")}`}
                      >
                        {c.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={item.label}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={`text-sm font-medium transition-colors hover:text-navy ${
                    location.pathname === item.to ? "text-navy" : "text-slate-600"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                data-testid="search-trigger"
                onClick={() => setSearchOpen(true)}
                className="h-9 w-9 inline-flex items-center justify-center rounded-md text-slate-600 hover:bg-subtle transition-colors"
                aria-label="Search"
              >
                <Search className="h-4 w-4" />
              </button>

              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="account-menu-trigger"
                      className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-sm font-medium text-slate-700 hover:bg-subtle transition-colors"
                    >
                      <User className="h-4 w-4" />
                      {user.name?.split(" ")[0] || "Account"}
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                      Profile
                    </DropdownMenuItem>
                    {user.role === "admin" && (
                      <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                        Admin Dashboard
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  to="/login"
                  data-testid="login-link"
                  className="hidden sm:inline-flex items-center h-9 px-3 rounded-md text-sm font-medium text-slate-700 hover:bg-subtle transition-colors"
                >
                  Login
                </Link>
              )}

              {!user && (
                <Link
                  to="/register"
                  data-testid="register-cta"
                  className="hidden sm:inline-flex items-center h-9 px-4 rounded-md bg-navy text-white text-sm font-medium hover:bg-navy-light transition-colors"
                >
                  Register
                </Link>
              )}

              <button
                data-testid="mobile-menu-trigger"
                onClick={() => setMobileOpen((o) => !o)}
                className="lg:hidden h-9 w-9 inline-flex items-center justify-center rounded-md text-navy hover:bg-subtle"
                aria-label="Menu"
              >
                {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>

        {mobileOpen && (
          <div className="lg:hidden border-t border-border bg-white" data-testid="mobile-menu">
            <div className="px-4 py-4 space-y-1">
              {dedupNav.map((item) => item.children ? (
                <div key={item.label} className="pt-1">
                  <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-400">{item.label}</p>
                  {item.children.map((c) => (
                    <Link key={c.label} to={c.to} className="block px-3 py-2.5 rounded-md text-sm font-medium text-slate-700 hover:bg-subtle">{c.label}</Link>
                  ))}
                </div>
              ) : (
                <Link
                  key={item.label}
                  to={item.to}
                  className="block px-3 py-2.5 rounded-md text-sm font-medium text-slate-700 hover:bg-subtle"
                >
                  {item.label}
                </Link>
              ))}
              <div className="pt-3 border-t border-border mt-3 flex flex-col gap-2">
                {user ? (
                  <>
                    <Link to="/dashboard" className="px-3 py-2.5 rounded-md text-sm font-medium text-slate-700 hover:bg-subtle">Profile</Link>
                    {user.role === "admin" && <Link to="/admin" className="px-3 py-2.5 rounded-md text-sm font-medium text-slate-700 hover:bg-subtle">Admin</Link>}
                    <button onClick={logout} className="text-left px-3 py-2.5 rounded-md text-sm font-medium text-slate-700 hover:bg-subtle">Logout</button>
                  </>
                ) : (
                  <Link to="/login" className="px-3 py-2.5 rounded-md text-sm font-medium text-slate-700 hover:bg-subtle">Login</Link>
                )}
                {!user && <Link to="/register" className="text-center px-3 py-2.5 rounded-md bg-navy text-white text-sm font-medium">Register</Link>}
              </div>
            </div>
          </div>
        )}
      </header>
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  );
}
